import type React from 'react';

import { ButtonGroup, FormField, Label, Textarea, ToggleButton } from '@zillow/constellation-10';

import { STORAGE } from '../../../../shared/constants/index';
import type { Section } from '../../../../shared/models/index';
import CurrencyInput from '../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Storage({ section, setSectionValues }: Props): JSX.Element {
  const isSpaceNone = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_NONE);
  const isSpaceIncluded = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_INCLUDED);
  const isSpaceAdditionalFee = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE);

  function onDescriptionChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [STORAGE.FIELD_NAME.SPACE_PROVIDED_DESCRIPTION]: value });
  }

  function onFeeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE_AMOUNT]: value });
  }

  function onNoneClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: true,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: false,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: false,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: false,
    });
  }

  function onIncludedClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: false,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: true,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: true,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: false,
    });
  }

  function onAdditionalClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: false,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: true,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: false,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: true,
    });
  }

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Is extra storage space available?</Label>}
        control={
          <ButtonGroup orientation="vertical" conjoined css={{ width: '100%' }} aria-label="storage available">
            <ToggleButton onClick={onNoneClick} selected={isSpaceNone}>
              No, none is available
            </ToggleButton>
            <ToggleButton onClick={onIncludedClick} selected={isSpaceIncluded}>
              Yes, included in rent
            </ToggleButton>
            <ToggleButton onClick={onAdditionalClick} selected={isSpaceAdditionalFee}>
              Yes, for an additional fee
            </ToggleButton>
          </ButtonGroup>
        }
      />
      {(isSpaceIncluded || isSpaceAdditionalFee) && (
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label id="area-and-location-description">Describe the area and its location</Label>}
          control={
            <Textarea
              aria-labelledby="area-and-location-description"
              css={{ resize: 'vertical' }}
              value={section.getFieldValue(STORAGE.FIELD_NAME.SPACE_PROVIDED_DESCRIPTION)}
              onChange={onDescriptionChange}
            />
          }
        />
      )}
      {isSpaceAdditionalFee && (
        <FormField
          required
          label={<Label id="storage-fee">Storage fee</Label>}
          control={
            <CurrencyInput
              aria-labelledby="storage-fee"
              data-test="storage-fee"
              value={section.getFieldValue(STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE_AMOUNT)}
              onChange={onFeeChange}
              endAdornment="per month"
            />
          }
        />
      )}
    </>
  );
}
